import { useCallback, useEffect, useMemo, useRef } from "react"

import { useQueryClient } from "@tanstack/react-query"
import type { SetterOrUpdater } from "recoil"
import {
  useSetRecoilState,
  useRecoilValue,
  useRecoilState,
  useRecoilCallback,
} from "recoil"
import { z } from "zod"

import { queryKeys, PROPOSAL_KEYS } from "~/api/cache"
import { useSSE } from "~/components/sse-notifier"
import {
  allAreasState,
  allEdgesState,
  allNodesState,
} from "~/containers/LegacyDesigner/state/elements"
import {
  allPanelsState,
  selectedModuleRemoteState,
  selectedModuleState,
} from "~/containers/LegacyDesigner/state/elements/panels"
import { areasOfPanelsIndexState } from "~/containers/LegacyDesigner/state/elements/relations"
import {
  phaseState,
  useEnterNextPhase,
} from "~/containers/LegacyDesigner/state/phase"
import {
  useDesignRequest,
  useDesignerDataByRequestId,
} from "~/containers/Proposal/state/design-request/use-design-request"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import {
  designSaveRecalculatingState,
  syncWaitingForEntityState,
  syncWaitingState,
} from "~/containers/Proposal/state/sync-state"
import { currentUserState } from "~/containers/Proposal/state/user/user-state"
import type { UpdateDesignInput } from "~/graphql/client"
import {
  useDesignGenabilityStorageIdQuery,
  useSavingsQuery,
  useEstimatesForDesignQuery,
  useGetProposalQuery,
  useDesignQuery,
  useUpdateDesignMutation,
  Permission,
  useResetDesignMutation,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { isNotEmpty } from "~/utils/fp"
import { getPowurUrl, getVisionUrl } from "~/utils/getUrlForEnvironment"
import { Index1ToMany } from "~/utils/join"
import { mapObject } from "~/utils/mapObject"
import type { Accessors } from "~/utils/mapToInputs"
import { createInputBuilder, get } from "~/utils/mapToInputs"
import { getFrom } from "~/utils/recoil"

import { designSchema, type Design, type InstallArea } from "./design-model"
import { useDesignOptimisticUpdate } from "./design-optimistic-update"
import {
  selectedDesignIdState,
  designDiffState,
  designLocalState,
  designRemoteState,
  estimatesRemoteState,
  savingsRemoteState,
} from "./design-state"
import { designInitialLoadCompleteState } from "./designInitialLoadComplete"
import { loadDesignIntoElements } from "./load-design-into-elements"
import {
  getSolarModuleEquipmentFromDesign,
  mapSolarModuleToSelectedModule,
} from "./solar-module"
import { useProposalFetcher } from "../proposal/proposal-fetchers"
import {
  isProposal,
  proposalLocalState,
  proposalRemoteState,
} from "../proposal/proposal-state"

export type RequireAtLeastOne<T> = {
  [K in keyof T]-?: Partial<Pick<T, Exclude<keyof T, K>>> & Required<Pick<T, K>>
}[keyof T]

export const useFetchDesignState = (
  object: RequireAtLeastOne<{
    designId: string
    designRequestId: string
    proposalSlug: string
  }>,
): Design | null => {
  const { proposalSlug, designId, designRequestId } = object
  const { client } = useGraphqlClient()
  const shouldSyncDesign = useRecoilValue(syncWaitingForEntityState(`design`))
  const shouldSyncEstimates = useRecoilValue(
    syncWaitingForEntityState(`estimates`),
  )
  const shouldSyncSavings = useRecoilValue(syncWaitingForEntityState(`savings`))
  const selectedDesignId = useRecoilValue(selectedDesignIdState)
  const [design, setRemote] = useRecoilState(designRemoteState)
  const setSyncWaiting = useSetRecoilState(syncWaitingState)
  const setEstimates = useSetRecoilState(estimatesRemoteState)
  const { slug } = useRecoilValue(proposalRemoteState)
  const { refetch: refetchProposal } = useProposalFetcher({ slug })
  const setSavings = useSetRecoilState(savingsRemoteState)
  const onError = useDispatchError()

  useSSE({
    channelNameSpec: `${design?.proposalId}`,
  })

  const { refetch: refetchDesignRequest } =
    useDesignerDataByRequestId(designRequestId)

  const designIdToFetch = proposalSlug
    ? selectedDesignId
    : designId
      ? designId
      : (design.id ?? ``)
  const designIdForDirectFetch = designRequestId ? `` : designIdToFetch

  const { refetch: refetchDesign } = useDesignQuery(
    client,
    { id: designIdForDirectFetch },
    {
      staleTime: Infinity,
      onError,
      enabled: !!designIdForDirectFetch,
      onSuccess: (data) => {
        try {
          const designParsed = designSchema.parse(data.design)
          setSyncWaiting((current) => ({ ...current, design: false }))
          setRemote(designParsed)
          // setLocal(designParsed)
        } catch (error) {
          if (error instanceof z.ZodError) {
            error.errors.forEach((err) => {
              console.error(`zod error:`, err)
            })
          }
          console.error(error)
          onError(error)

          return
        }
      },
    },
  )

  useEffect(() => {
    if (shouldSyncDesign && designRequestId) {
      void refetchDesignRequest().then((result) => {
        if (result.isSuccess) {
          setSyncWaiting((current) => ({ ...current, design: false }))
        }
      })

      return
    }

    if (shouldSyncDesign && designIdForDirectFetch) {
      void refetchDesign()
    }
  }, [
    designIdForDirectFetch,
    designRequestId,
    refetchDesign,
    refetchDesignRequest,
    setSyncWaiting,
    shouldSyncDesign,
  ])

  const { refetch: refetchEstimates } = useEstimatesForDesignQuery(
    client,
    { designId: designIdToFetch },
    {
      staleTime: Infinity,
      enabled: !!designIdToFetch,
      onError,
      onSuccess: (data) => {
        const { estimatesForDesign } = data
        setEstimates(estimatesForDesign)
        setSyncWaiting((prev) => ({ ...prev, estimates: false }))
        console.count(`useEstimates`)
      },
    },
  )

  const refreshEstimates = useCallback(async () => {
    if (shouldSyncEstimates && designIdToFetch) {
      await refetchEstimates()
    }
  }, [refetchEstimates, designIdToFetch, shouldSyncEstimates])

  useEffect(() => {
    void refreshEstimates()
  }, [refreshEstimates])

  const { refetch: refetchSavings } = useSavingsQuery(
    client,
    { designId: designIdToFetch },
    {
      staleTime: Infinity,
      enabled: Boolean(designIdToFetch),
      onSuccess: (data) => {
        const { savingsForDesignId } = data
        setSavings(savingsForDesignId)
      },
    },
  )

  const { refetch: refetchDesignGenabilityStorageId } =
    useDesignGenabilityStorageIdQuery(
      client,
      { id: designIdToFetch },
      {
        staleTime: Infinity,
        onError,
        enabled: false,
        onSuccess: async (data) => {
          try {
            const { profiles } = data.design
            if (data.design.profiles.length > 0) {
              setRemote((current) => ({
                ...current,
                profiles,
              }))
            }
            setSyncWaiting((prev) => ({ ...prev, savings: false }))
            await refetchProposal()
          } catch (error) {
            onError(error)

            return
          }
        },
      },
    )

  const refreshSavings = useCallback(async () => {
    if (shouldSyncSavings && designIdToFetch) {
      try {
        await refetchSavings()
        await refetchDesignGenabilityStorageId()
      } catch {
        // Clear the sync flag even on failure so the page isn't stuck in a
        // permanent loading state (blocks PDF generation via WINDOW_IS_LOADED).
        setSyncWaiting((prev) => ({ ...prev, savings: false }))
      }
    }
  }, [
    refetchSavings,
    refetchDesignGenabilityStorageId,
    shouldSyncSavings,
    designIdToFetch,
    setSyncWaiting,
  ])

  useEffect(() => {
    void refreshSavings()
  }, [refreshSavings])

  const lastDesignRequestId = useRef(designRequestId)
  const lastDesignId = useRef(designId)

  useEffect(() => {
    if (designRequestId && designRequestId !== lastDesignRequestId.current) {
      void refetchDesignRequest()
      lastDesignRequestId.current = designRequestId
    } else if (designId && designId !== lastDesignId.current) {
      void refetchDesign()
      lastDesignId.current = designId
    }
  }, [
    proposalSlug,
    refetchDesign,
    designRequestId,
    refetchDesignRequest,
    designId,
  ])

  return design
}

const updateDesignInputAccessors: Accessors<Design, UpdateDesignInput> = {
  id: (design) => design.id ?? ``,
  name: (design) => design.name,
  edges: get,
  installAreas: get,
  installAreasToPanels: get,
  inverterId: (design) => design.inverterId,
  secondaryInverterId: (design) => design.secondaryInverterId,
  nodes: get,
  obstructions: get,
  setbacks: get,
  panels: get,
  batteryCompatibility: (design) => design.batteryCompatibility,
  inverterManufacturer: (design) => design.inverterManufacturer,
  selectedDesignEquipmentIds: (design) =>
    design.designEquipment?.map((de) => de.equipment?.id).filter(isNotEmpty),
  phase: (design) => design.phase,
  completedAt: (design) => design.completedAt,
  shadingAreas: get,
  energyBackupId: (design) => design.energyBackupId,
  tool: (design) => design.tool,
  type: (design) => design.type,
  energyEfficiencyKitRebate: (design) =>
    typeof design.energyEfficiencyKitRebate === `boolean`
      ? design.energyEfficiencyKitRebate
      : undefined,
  energyEfficiencyKitRebateAmount: (design) =>
    design.energyEfficiencyKitRebateAmount
      ? parseFloat(`${design.energyEfficiencyKitRebateAmount}`)
      : undefined,
  treeTrimmingRebate: (design) =>
    typeof design.treeTrimmingRebate === `boolean`
      ? design.treeTrimmingRebate
      : undefined,
  treeTrimmingRebateAmount: (design) =>
    design.treeTrimmingRebateAmount
      ? parseFloat(`${design.treeTrimmingRebateAmount}`)
      : undefined,
  homeownerRebate: (design) =>
    typeof design.homeownerRebate === `boolean`
      ? design.homeownerRebate
      : undefined,
  homeownerRebateAmount: (design) =>
    design.homeownerRebateAmount
      ? parseFloat(`${design.homeownerRebateAmount}`)
      : undefined,
  roofWorkRebate: (design) =>
    typeof design.roofWorkRebate === `boolean`
      ? design.roofWorkRebate
      : undefined,
  roofWorkRebateAmount: (design) =>
    design.roofWorkRebateAmount
      ? parseFloat(`${design.roofWorkRebateAmount}`)
      : undefined,
  brochurePositionX: (design) => design.brochurePositionX,
  brochurePositionY: (design) => design.brochurePositionY,
  brochureZoom: (design) => design.brochureZoom,
  // evOutlet: (design) => design.evOutlet,
  // evCharger: (design) => design.evCharger,
  displayImageryProvider: (design) => design.displayImageryProvider,
}

export const createUpdateDesignInput = createInputBuilder<
  Design,
  UpdateDesignInput
>(updateDesignInputAccessors)

export type SaveDesign = VoidFunction | (() => Promise<void>)

export type UseDesign = () => {
  remote: Design
  local: Design
  setLocal: SetterOrUpdater<Design>
  save: SaveDesign
  changeLocal: (update: Partial<Design>) => void
  reset: VoidFunction
  resetRemote: VoidFunction
  resetLocal: VoidFunction
  isDirty: boolean
  delta: Partial<Design> | null
  isSaving: boolean
  isResetting: boolean
  isSuccess: boolean
}

const useLoadDesignIntoElements = (): ((design: Design) => void) =>
  useRecoilCallback(
    ({ set }) =>
      (design: Design) => {
        loadDesignIntoElements(set, design)
      },
    [],
  )

export const useSyncDesign = (): void => {
  const [designInitialLoadComplete, setDesignInitialLoadComplete] =
    useRecoilState(designInitialLoadCompleteState)

  const remote = useRecoilValue(designRemoteState)
  const setSelectedModule = useSetRecoilState(selectedModuleState)
  const setSelectedModuleRemote = useSetRecoilState(selectedModuleRemoteState)
  const setLocal = useSetRecoilState(designLocalState)
  const { id } = remote

  const allNodes = useRecoilValue(allNodesState)
  const allEdges = useRecoilValue(allEdgesState)
  const allAreas = useRecoilValue(allAreasState)
  const allPanels = useRecoilValue(allPanelsState)
  const areasOfPanelsIndex = useRecoilValue(areasOfPanelsIndexState)
  const loadDesign = useLoadDesignIntoElements()

  const areaPanelString = JSON.stringify(areasOfPanelsIndex.toJson())

  useEffect(() => {
    if (id) {
      if (!designInitialLoadComplete) {
        setDesignInitialLoadComplete(true)
        loadDesign(remote)
      } else {
        const solarModule = getSolarModuleEquipmentFromDesign(remote)
        if (solarModule) {
          const mapped = mapSolarModuleToSelectedModule(solarModule)
          setSelectedModule(mapped)
          setSelectedModuleRemote(mapped)
        }
        setLocal((localDesign: Design) => ({
          ...localDesign,
          installAreas: mapObject(
            localDesign.installAreas,
            (
              {
                nodeIds,
                mountLocation,
                azimuth = null,
                azimuthLocked,
                slope,
                material,
                manualShading,
                preferSunroof,
              },
              key,
            ): InstallArea => {
              const { sunroofShading } = remote.installAreas[key] ?? {
                sunroofShading: null,
              }

              return {
                nodeIds,
                mountLocation,
                azimuth,
                azimuthLocked,
                slope,
                material,
                manualShading,
                preferSunroof,
                sunroofShading,
              }
            },
          ),
        }))
      }
    }
  }, [
    id,
    designInitialLoadComplete,
    setDesignInitialLoadComplete,
    remote,
    setLocal,
    loadDesign,
    setSelectedModule,
    setSelectedModuleRemote,
  ])

  useEffect(() => {
    setLocal((design: Design) => (id ? { ...design, nodes: allNodes } : design))
  }, [id, setLocal, allNodes])

  useEffect(() => {
    setLocal((design: Design) => (id ? { ...design, edges: allEdges } : design))
  }, [id, setLocal, allEdges])

  useEffect(() => {
    setLocal((design: Design) =>
      id ? { ...design, installAreas: allAreas } : design,
    )
  }, [id, setLocal, allAreas])

  useEffect(() => {
    setLocal((design: Design) =>
      id ? { ...design, panels: allPanels } : design,
    )
  }, [id, setLocal, allPanels])

  useEffect(() => {
    setLocal((design: Design) =>
      id
        ? { ...design, installAreasToPanels: areasOfPanelsIndex.toJson() }
        : design,
    )
  }, [id, setLocal, areasOfPanelsIndex, areaPanelString])
}

const useSetProposal = () => useSetRecoilState(proposalRemoteState)

export type DesignActions = {
  setLocal: SetterOrUpdater<Design>
  changeLocal: (update: Partial<Design>) => void
  save: SaveDesign
  reset: VoidFunction
  resetLocal: VoidFunction
  resetRemote: VoidFunction
}

export type DesignStatus = {
  isSaving: boolean
  isResetting: boolean
  isSuccess: boolean
}

export const useDesignMachinery = (): {
  actions: DesignActions
  status: DesignStatus
} => {
  const { client } = useGraphqlClient()
  const setRemote = useSetRecoilState(designRemoteState)
  const local = useRecoilValue(designLocalState)
  const setLocal = useSetRecoilState(designLocalState)
  const loadDesign = useLoadDesignIntoElements()
  const { slug } = useRecoilValue(proposalLocalState)
  const onError = useDispatchError()
  const setDesignSaveRecalculating = useSetRecoilState(
    designSaveRecalculatingState,
  )

  const setProposal = useSetProposal()
  const queryClient = useQueryClient()

  const { refetch: refreshProposal } = useGetProposalQuery(
    client,
    { findProposalInput: { slug } },
    {
      staleTime: Infinity,
      enabled: false,
      onSuccess: (data) => {
        if (isProposal(data.proposal)) {
          setProposal(data.proposal)

          queryClient.invalidateQueries(PROPOSAL_KEYS.pricing(data.proposal.id))
          // Invalidate LightReach pricing when proposal changes
          queryClient.invalidateQueries(
            queryKeys.lightReach.byProposal(data.proposal.id),
          )
          queryClient.invalidateQueries(queryKeys.vppPrograms.all)
        }
      },
      onError,
    },
  )

  const localRef = useRef<Design>(local)

  useDesignOptimisticUpdate(localRef)

  // now this is trying to update design that doesn't exist i guess
  const invalidateBaseEquipment = () =>
    void queryClient.invalidateQueries({
      queryKey: queryKeys.legacy.graphql.baseEquipmentForProposal,
    })

  const {
    isLoading: isSaving,
    mutateAsync,
    isSuccess,
  } = useUpdateDesignMutation(client, {
    onSuccess: (data) => {
      try {
        setRemote(designSchema.parse(data.updateDesign))
        invalidateBaseEquipment()
        // setLocal(designSchema.parse(data.updateDesign))
      } catch (e) {
        return onError(e)
      }
    },
    onError,
  })

  // now this is trying to update design that doesn't exist i guess
  const { mutateAsync: resetDesign, isLoading: isResetting } =
    useResetDesignMutation(client, {
      onSuccess: (data) => {
        try {
          const design = designSchema.parse(data.resetDesign)
          setRemote(design)
          loadDesign(design)
        } catch (e) {
          return onError(e)
        }
      },
      onError,
    })

  // Ref instead of a dep so `save` keeps a stable identity while still
  // reading the current saving flag at call time.
  const isSavingRef = useRef(isSaving)
  useEffect(() => {
    isSavingRef.current = isSaving
  }, [isSaving])

  const resetRemote = useRecoilCallback(
    ({ snapshot }) => {
      const get = getFrom(snapshot)

      return async () => {
        const { id: idFromSnapshot } = get(designLocalState)
        if (idFromSnapshot) {
          await resetDesign({ id: idFromSnapshot })
          await refreshProposal()
        } else {
          console.warn(`No design to reset`)
        }
      }
    },
    [resetDesign, refreshProposal],
  )

  const save = useRecoilCallback(
    ({ snapshot }) => {
      const get = getFrom(snapshot)

      return async () => {
        const deltaFromSnapshot = get(designDiffState)
        const localDesignSnapshot = get(designLocalState)
        const { id: idFromSnapshot } = localDesignSnapshot
        localRef.current = localDesignSnapshot

        if (deltaFromSnapshot && !isSavingRef.current) {
          const input = createUpdateDesignInput(
            deltaFromSnapshot,
            idFromSnapshot,
          )
          setRemote((r) => ({ ...r, ...deltaFromSnapshot }))
          setDesignSaveRecalculating(true)
          await mutateAsync({ input })
          await refreshProposal()
        } else {
          console.warn(`No changes to save`)
        }
      }
    },
    [mutateAsync, refreshProposal, setDesignSaveRecalculating],
  )

  const changeLocal = useCallback(
    (update: Partial<Design>) => {
      setLocal((design: Design) => ({ ...design, ...update }))
    },
    [setLocal],
  )

  const reset = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        set(designLocalState, getFrom(snapshot)(designRemoteState))
      },
    [],
  )

  const resetLocal = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        loadDesignIntoElements(set, getFrom(snapshot)(designRemoteState))
      },
    [],
  )

  const actions = useMemo(
    () => ({ setLocal, changeLocal, save, reset, resetLocal, resetRemote }),
    [setLocal, changeLocal, save, reset, resetLocal, resetRemote],
  )

  const status = useMemo(
    () => ({ isSaving, isResetting, isSuccess }),
    [isSaving, isResetting, isSuccess],
  )

  return { actions, status }
}

export const useDesign: UseDesign = () => {
  const { actions, status } = useDesignMachinery()
  const local = useRecoilValue(designLocalState)
  const remote = useRecoilValue(designRemoteState)
  const delta = useRecoilValue(designDiffState)

  return useMemo(
    () => ({
      ...actions,
      ...status,
      local,
      remote,
      delta,
      isDirty: delta !== null,
    }),
    [actions, status, local, remote, delta],
  )
}

export const useSaveDesign = (): {
  save: SaveDesign
  resetRemote: VoidFunction
  isSaving: boolean
  isResetting: boolean
  isSuccess: boolean
} => {
  const { client } = useGraphqlClient()
  const setRemote = useSetRecoilState(designRemoteState)
  const setLocal = useSetRecoilState(designLocalState)

  const setProposal = useSetProposal()
  const setAllNodes = useSetRecoilState(allNodesState)
  const setAllEdges = useSetRecoilState(allEdgesState)
  const setAllAreas = useSetRecoilState(allAreasState)
  const setAllPanels = useSetRecoilState(allPanelsState)
  const setAreasOfPanelsIndex = useSetRecoilState(areasOfPanelsIndexState)
  const { slug } = useRecoilValue(proposalLocalState)
  const onError = useDispatchError()
  const queryClient = useQueryClient()
  const setDesignSaveRecalculating = useSetRecoilState(
    designSaveRecalculatingState,
  )
  // now this is trying to update design that doesn't exist i guess
  const invalidateBaseEquipment = () =>
    void queryClient.invalidateQueries({
      queryKey: queryKeys.legacy.graphql.baseEquipmentForProposal,
    })

  const {
    isLoading: isSaving,
    mutateAsync,
    isSuccess,
  } = useUpdateDesignMutation(client, {
    onSuccess: (data) => {
      try {
        const parsedDesign = designSchema.parse(data.updateDesign)
        setRemote(parsedDesign)
        setLocal(parsedDesign)
        invalidateBaseEquipment()
      } catch (e) {
        return onError(e)
      }
    },
    onError,
  })

  const { refetch: refreshProposal } = useGetProposalQuery(
    client,
    { findProposalInput: { slug } },
    {
      staleTime: Infinity,
      enabled: false,
      onSuccess: (data) => {
        if (isProposal(data.proposal)) {
          setProposal(data.proposal)
        }
      },
      onError,
    },
  )

  // now this is trying to update design that doesn't exist i guess
  const { mutateAsync: resetDesign, isLoading: isResetting } =
    useResetDesignMutation(client, {
      onSuccess: (data) => {
        try {
          const design = designSchema.parse(data.resetDesign)
          setRemote(design)
          setLocal(design)
          setAllNodes(design.nodes)
          setAllEdges(design.edges)
          setAllAreas(design.installAreas as Record<string, InstallArea>)
          setAllPanels(design.panels)
          setAreasOfPanelsIndex(new Index1ToMany(design.installAreasToPanels))
        } catch (e) {
          return onError(e)
        }
      },
      onError,
    })

  const resetRemote = useRecoilCallback(
    ({ snapshot }) => {
      const get = getFrom(snapshot)

      return async () => {
        const { id: idFromSnapshot } = get(designLocalState)
        if (idFromSnapshot) {
          await resetDesign({ id: idFromSnapshot })
          await refreshProposal()
        } else {
          console.warn(`No design to reset`)
        }
      }
    },
    [resetDesign, refreshProposal],
  )

  const save = useRecoilCallback(
    ({ snapshot }) => {
      const get = getFrom(snapshot)

      return async () => {
        const deltaFromSnapshot = get(designDiffState)
        const { id: idFromSnapshot } = get(designLocalState)

        if (deltaFromSnapshot && !isSaving) {
          const input = createUpdateDesignInput(
            deltaFromSnapshot,
            idFromSnapshot,
          )
          setDesignSaveRecalculating(true)
          await mutateAsync({ input })
          await refreshProposal()
        } else {
          console.warn(`No changes to save`)
        }
      }
    },
    [mutateAsync, refreshProposal, isSaving, setDesignSaveRecalculating],
  )

  return {
    save,
    resetRemote,
    isSaving,
    isResetting,
    isSuccess,
  }
}

export const useChangeLocalDesign = (): ((update: Partial<Design>) => void) => {
  const setLocal = useSetRecoilState(designLocalState)

  return (update: Partial<Design>) => {
    setLocal((design: Design) => ({ ...design, ...update }))
  }
}

/**
 * Enters the next design phase and saves. Marking the Design completed (the
 * Panel Placement exit) stamps `completedAt` first.
 */
export const enterNextPhaseAndSave = async ({
  enterNextPhase,
  save,
  changeLocal,
  markCompleted,
}: {
  enterNextPhase: VoidFunction
  save: SaveDesign
  changeLocal: (update: Partial<Design>) => void
  markCompleted: boolean
}): Promise<void> => {
  if (markCompleted) {
    changeLocal({ completedAt: new Date() })
  }
  enterNextPhase()
  // Recoil transactions settle on the next tick; save after them.
  await new Promise((resolve) => setTimeout(resolve, 0))
  await save()
}

export const useCompleteDesign = ({
  design,
}: {
  design: ReturnType<UseDesign>
}): (({ completeAndNext }: { completeAndNext: boolean }) => Promise<void>) => {
  const { save, changeLocal } = design
  const currentUser = useRecoilValue(currentUserState)
  const enterNextPhase = useEnterNextPhase()
  const { complete: completeOnly, completeAndClaimNext } = useDesignRequest()
  const currentPhase = useRecoilValue(phaseState)

  const completeClaimNextAndRedirect = async () => {
    const next = await completeAndClaimNext()
    if (next) {
      window.location.replace(
        `${getVisionUrl()}/designer/service-queue/${
          next.completeDesignRequestAndClaimNext.id
        }`,
      )
    } else {
      window.location.replace(`${getPowurUrl()}/admin-solar-proposals/vision`)
    }
  }

  const completeBackToQueue = async () => {
    await completeOnly()
    window.location.replace(`${getPowurUrl()}/admin-solar-proposals/vision`)
  }

  const continueOrComplete = async ({
    completeAndNext,
  }: {
    completeAndNext: boolean
  }) => {
    const isPanelPlacement = currentPhase === `Panel Placement`
    await enterNextPhaseAndSave({
      enterNextPhase,
      save,
      changeLocal,
      markCompleted: isPanelPlacement,
    })
    if (!isPanelPlacement) {
      return
    }
    const allowedPermissions: (Permission | `${Permission}`)[] = [
      Permission.vision_service_bureau_user,
      Permission.vision_service_bureau_admin,
      Permission.vision_admin,
    ]
    if (
      currentUser.permissions.some((permission) =>
        allowedPermissions.includes(permission),
      ) &&
      window.location.pathname.includes(`service-queue`)
    ) {
      completeAndNext
        ? void completeClaimNextAndRedirect()
        : void completeBackToQueue()

      return
    }
  }

  return continueOrComplete
}
